/**
 * The alerts page (container, phase 7b item 4): what holds now and the
 * history, both live through the change channel (D35: an `alert` change
 * refetches them), and — for a tenant admin — the way to the notification
 * settings. On a node the list says alerts are the cloud's.
 */
import type { AlertType } from '@norna/tenant-alerts/catalog.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAlertHistory, useAlerts, useGrants } from '../app/hooks.js';
import { CloudOnlyGate } from './CloudOnlyPage.js';
import { AlertHistory } from '../components/alerts/AlertHistory.js';
import { AlertList } from '../components/alerts/AlertList.js';
import { allows } from '../lib/permissions.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export const ALERT_SETTINGS_HREF = '/alerts/settings';

/** A cloud-only screen (`RouteDef.hosts`): its calls go to the cloud from a node's page too; "needs a cloud connection" where they cannot. */
export function AlertsView() {
  return (
    <CloudOnlyGate route="listAlerts" what="Alerts" path="/alerts">
      <AlertsViewPage />
    </CloudOnlyGate>
  );
}

function AlertsViewPage() {
  const alerts = useAlerts('active');
  const [type, setType] = useState<AlertType | null>(null);
  const history = useAlertHistory(type);
  const grants = useGrants();
  const navigate = useNavigate();
  const available = alerts.data?.available ?? true;
  return (
    <div className="mx-auto grid max-w-6xl gap-6" data-testid="alerts-page">
      <AlertList
        available={available}
        alerts={alerts.data?.available === true ? alerts.data.alerts : []}
        loading={alerts.isFetching}
        error={alerts.error === null ? null : messageOf(alerts.error)}
        canConfigure={available && grants !== null && allows(grants, 'tenant.admin', { siteId: null, studioId: null })}
        onRetry={() => {
          void alerts.refetch();
        }}
        onConfigure={() => {
          void navigate({ to: ALERT_SETTINGS_HREF });
        }}
      />
      {available ? (
        <AlertHistory
          entries={(history.data?.pages ?? []).flatMap((p) => p.entries)}
          loading={history.isFetching && !history.isFetchingNextPage}
          error={history.error === null ? null : messageOf(history.error)}
          type={type}
          hasMore={history.hasNextPage}
          loadingMore={history.isFetchingNextPage}
          onType={setType}
          onLoadMore={() => {
            void history.fetchNextPage();
          }}
        />
      ) : null}
    </div>
  );
}
